import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { auth } from '@onevision-resources/login';
import {
  ApolloLink,
  createHttpLink,
  createTtlLink,
  from,
  NextLink,
  Operation,
  useGraphQLConfig,
} from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { config } from 'src/core/config';
import { useGlobalStore } from 'src/store';
import useSnackbarStore from 'src/store/snackbar';
import { queryRegistryLink } from './query-registry-link';
import { versionLink } from './version-link';

const APP_VERSION_HEADER = 'x-app-version';
const APP_ORIGIN_HEADER = 'ov-app-id';

const DEFAULT_HEADERS = {
  'x-api-key': import.meta.env.VITE_LOCAL_API_KEY,
  [APP_VERSION_HEADER]: config.appVersion,
  [APP_ORIGIN_HEADER]: 'portal',
};

const PARTNER_IDS_HEADER = 'ov-partner-ids';

export const defaultConfig = {
  uri: import.meta.env.VITE_GRAPHQL_API_URL,
  headers: DEFAULT_HEADERS,
  devtools: {
    enabled: import.meta.env.VITE_APP_ENV === 'local',
  },
  defaultOptions: {
    // `useQuery` hook is using `watchQuery` function options (not `query`)
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
} as const;

const errorLink = onError(({ networkError, operation }) => {
  if (networkError && 'statusCode' in networkError) {
    switch (networkError.statusCode) {
      case 401:
        if (!operation.getContext().suppressUnauthorizedToast) {
          useSnackbarStore.getState().showSnackbar('You are not authorized for this request', {
            severity: 'error',
            autoHide: null,
          });
        }
        console.error('401 Error:', networkError);
        break;
      default:
        console.error('Network Error:', networkError);
        break;
    }
  }
});

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_GRAPHQL_API_URL,
});

const authLink = setContext(async (_, context) => {
  const token = await auth.getToken();
  const { selectedPartnerId } = useGlobalStore.getState();
  const { omitIsolationModeHeader, includePlatformLevelData } = context;

  const setIsolationModeHeader = !isNullish(selectedPartnerId) && !omitIsolationModeHeader;
  const partnerIds =
    selectedPartnerId === PLATFORM_PARTNER_ID || !includePlatformLevelData
      ? [selectedPartnerId]
      : [selectedPartnerId, PLATFORM_PARTNER_ID];

  return {
    headers: {
      ...DEFAULT_HEADERS,
      Authorization: `Bearer ${token}`,
      ...(setIsolationModeHeader
        ? {
            [PARTNER_IDS_HEADER]: partnerIds.join(','),
          }
        : {}),
    },
  };
});

const tenantLink = new ApolloLink((operation: Operation, forward: NextLink) => {
  const { selectedPartnerId } = useGlobalStore.getState();

  const partnerId = isNullish(selectedPartnerId) ? '*' : String(selectedPartnerId);

  operation.setContext(() => ({
    __partnerId: partnerId,
  }));

  // appending __partnerId into variables so it would be automatically
  // referenced in all apollo links including ttl
  operation.variables = {
    ...operation.variables,
    __partnerId: partnerId,
  };

  return forward(operation);
});

const ttlLinkInstance = createTtlLink();

/**
 * Pay attention that ttlLink used here will be catching every query operation for ttl period.
 * Use `ttl` context parameter to override this behavior, and if `no-cache` policy is needed –
 * set `ttl: 0` in the context additionally.
 */
export const useGraphQLClientConfig = () => {
  const { config } = useGraphQLConfig(defaultConfig);

  const configWithAuth = useMemo(() => {
    return {
      ...config,
      link: from([
        // check app version mismatches
        versionLink,
        // append partner id into the context
        tenantLink,
        // track query for further invalidation purposes
        queryRegistryLink,
        // separate cache layer with ttl mechanism
        ttlLinkInstance.link,
        // error catcher
        errorLink,
        // authentication middleware
        authLink,
        // api middleware
        httpLink,
      ]),
    };
  }, [config]);

  return { config: configWithAuth };
};

export const ttlLink = ttlLinkInstance;
